import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Filter, Save, X, ChevronDown, Star, Trash2, Check } from 'lucide-react';
import { useSavedFilters, FilterValue, SavedFilter } from '@/hooks/useSavedFilters';
import { cn } from '@/lib/utils';

export interface FilterFieldConfig {
  field: string;
  label: string;
  type: 'text' | 'select' | 'multi-select' | 'number' | 'date' | 'boolean';
  options?: { label: string; value: string }[];
  // For async options loading (e.g., skills from database)
  fetchOptions?: () => Promise<{ label: string; value: string }[]>;
}

interface UniversalFiltersProps {
  entityType: string;
  fields: FilterFieldConfig[];
  onFiltersChange: (filters: FilterValue[]) => void;
  className?: string;
}

export function UniversalFilters({ entityType, fields, onFiltersChange, className }: UniversalFiltersProps) {
  const {
    savedFilters,
    activeFilters,
    setActiveFilters,
    saveFilter,
    updateFilter,
    deleteFilter,
    applyFilter,
    clearFilters,
    loading
  } = useSavedFilters(entityType);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [asyncOptions, setAsyncOptions] = useState<Record<string, { label: string; value: string }[]>>({});

  // Load async options for fields that need them
  useEffect(() => {
    fields.forEach(async (field) => {
      if (field.fetchOptions) {
        const options = await field.fetchOptions();
        setAsyncOptions(prev => ({ ...prev, [field.field]: options }));
      }
    });
  }, [fields]);

  // Notify parent when filters change
  useEffect(() => {
    onFiltersChange(activeFilters);
  }, [activeFilters, onFiltersChange]);

  const getFieldOptions = (field: FilterFieldConfig) => {
    return field.options || asyncOptions[field.field] || [];
  };

  const addFilter = (field: string) => {
    const fieldConfig = fields.find(f => f.field === field);
    if (!fieldConfig) return;

    const newFilter: FilterValue = {
      field,
      operator: fieldConfig.type === 'multi-select' ? 'in' : 'eq',
      value: fieldConfig.type === 'multi-select' ? [] : '',
      logic: 'OR'
    };
    setActiveFilters([...activeFilters, newFilter]);
  };

  const updateFilterValue = (index: number, updates: Partial<FilterValue>) => {
    setActiveFilters(prev => prev.map((f, i) => 
      i === index ? { ...f, ...updates } : f
    ));
  };

  const removeFilter = (index: number) => {
    setActiveFilters(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveFilter = async () => {
    if (!filterName.trim()) return;
    await saveFilter(filterName, activeFilters, setAsDefault);
    setSaveDialogOpen(false);
    setFilterName('');
    setSetAsDefault(false);
  };

  const renderFilterInput = (filter: FilterValue, index: number) => {
    const fieldConfig = fields.find(f => f.field === filter.field);
    if (!fieldConfig) return null;

    const options = getFieldOptions(fieldConfig);

    switch (fieldConfig.type) {
      case 'multi-select':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <MultiSelectFilter
                options={options}
                value={Array.isArray(filter.value) ? filter.value : []}
                onChange={(value) => updateFilterValue(index, { value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Match:</span>
              <Button
                variant={filter.logic === 'OR' ? 'default' : 'outline'}
                size="sm"
                className="h-6 text-xs"
                onClick={() => updateFilterValue(index, { logic: 'OR' })}
              >
                ANY
              </Button>
              <Button
                variant={filter.logic === 'AND' ? 'default' : 'outline'}
                size="sm"
                className="h-6 text-xs"
                onClick={() => updateFilterValue(index, { logic: 'AND' })}
              >
                ALL
              </Button>
            </div>
          </div>
        );
      case 'select':
        return (
          <Select
            value={filter.value}
            onValueChange={(value) => updateFilterValue(index, { value })}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select value" />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'number':
        return (
          <div className="flex items-center gap-2">
            <Select
              value={filter.operator}
              onValueChange={(value) => updateFilterValue(index, { operator: value as FilterValue['operator'] })}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="eq">=</SelectItem>
                <SelectItem value="gt">&gt;</SelectItem>
                <SelectItem value="lt">&lt;</SelectItem>
                <SelectItem value="gte">≥</SelectItem>
                <SelectItem value="lte">≤</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number"
              value={filter.value}
              onChange={(e) => updateFilterValue(index, { value: e.target.value })}
              className="w-[120px]"
            />
          </div>
        );
      case 'boolean':
        return (
          <Select
            value={filter.value?.toString()}
            onValueChange={(value) => updateFilterValue(index, { value: value === 'true' })}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Yes</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        );
      case 'date':
        return (
          <Input
            type="date"
            value={filter.value}
            onChange={(e) => updateFilterValue(index, { value: e.target.value })}
            className="w-[180px]"
          />
        );
      default:
        return (
          <Input
            value={filter.value}
            onChange={(e) => updateFilterValue(index, { value: e.target.value })}
            placeholder="Enter value..."
            className="w-[180px]"
          />
        );
    }
  };

  const getFilterLabel = (filter: FilterValue) => {
    const fieldConfig = fields.find(f => f.field === filter.field);
    if (!fieldConfig) return filter.field;

    if (fieldConfig.type === 'multi-select' && Array.isArray(filter.value)) {
      const options = getFieldOptions(fieldConfig);
      const selectedLabels = filter.value.map(v => 
        options.find(o => o.value === v)?.label || v
      );
      return `${fieldConfig.label}: ${selectedLabels.slice(0, 2).join(', ')}${selectedLabels.length > 2 ? ` +${selectedLabels.length - 2}` : ''} (${filter.logic})`;
    }

    if (fieldConfig.type === 'select') {
      const options = getFieldOptions(fieldConfig);
      const label = options.find(o => o.value === filter.value)?.label || filter.value;
      return `${fieldConfig.label}: ${label}`;
    }

    return `${fieldConfig.label}: ${filter.value}`;
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-2 flex-wrap">
        {/* Saved Filters Dropdown */}
        {savedFilters.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Star className="h-4 w-4 mr-2" />
                Saved Filters
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {savedFilters.map(filter => (
                <DropdownMenuItem 
                  key={filter.id} 
                  className="flex items-center justify-between"
                  onClick={() => applyFilter(filter)}
                >
                  <span className="flex items-center gap-2">
                    {filter.is_default && <Star className="h-3 w-3 fill-current text-yellow-500" />}
                    {filter.name}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteFilter(filter.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Add Filter Dropdown */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Add Filter
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-2">
            {fields.map(field => (
              <Button
                key={field.field}
                variant="ghost"
                className="w-full justify-start text-sm"
                onClick={() => addFilter(field.field)}
              >
                {field.label}
              </Button>
            ))}
          </PopoverContent>
        </Popover>

        {/* Save Current Filters */}
        {activeFilters.length > 0 && (
          <>
            <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Save className="h-4 w-4 mr-2" />
                  Save Filter
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Save Filter</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="filterName">Filter Name</Label>
                    <Input
                      id="filterName"
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                      placeholder="e.g., Senior React Developers"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="setDefault"
                      checked={setAsDefault}
                      onCheckedChange={setSetAsDefault}
                    />
                    <Label htmlFor="setDefault">Set as default filter</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveFilter} disabled={!filterName.trim() || loading}>
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4 mr-2" />
              Clear All
            </Button>
          </>
        )}
      </div>

      {/* Active Filters */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2">
          {activeFilters.map((filter, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-muted rounded-lg p-2"
            >
              <span className="text-sm font-medium">
                {fields.find(f => f.field === filter.field)?.label}:
              </span>
              {renderFilterInput(filter, index)}
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => removeFilter(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Active filter badges (collapsed view) */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {activeFilters.filter(f => f.value && (Array.isArray(f.value) ? f.value.length > 0 : true)).map((filter, index) => (
            <Badge key={index} variant="secondary" className="text-xs">
              {getFilterLabel(filter)}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// Multi-select component for skills and similar fields
function MultiSelectFilter({
  options,
  value,
  onChange
}: {
  options: { label: string; value: string }[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const [search, setSearch] = useState('');

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggleOption = (optValue: string) => {
    if (value.includes(optValue)) {
      onChange(value.filter(v => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-[250px] justify-between">
          {value.length > 0 ? `${value.length} selected` : 'Select items...'}
          <ChevronDown className="h-4 w-4 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8"
          />
        </div>
        <div className="max-h-[300px] overflow-auto p-1">
          {filteredOptions.slice(0, 50).map(opt => (
            <div
              key={opt.value}
              className={cn(
                "flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded hover:bg-muted",
                value.includes(opt.value) && "bg-primary/10"
              )}
              onClick={() => toggleOption(opt.value)}
            >
              <div className={cn(
                "h-4 w-4 border rounded flex items-center justify-center",
                value.includes(opt.value) && "bg-primary border-primary"
              )}>
                {value.includes(opt.value) && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
              <span className="text-sm">{opt.label}</span>
            </div>
          ))}
          {filteredOptions.length === 0 && (
            <p className="text-sm text-muted-foreground p-2">No results found</p>
          )}
          {filteredOptions.length > 50 && (
            <p className="text-xs text-muted-foreground p-2">
              Showing first 50 results. Refine your search.
            </p>
          )}
        </div>
        {value.length > 0 && (
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => onChange([])}
            >
              Clear selection
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default UniversalFilters;
